import React, { useEffect, useState } from 'react';
import PageHeader from '../components/ui/PageHeader';
import styled from 'styled-components';
import { FaPhone, FaFax, FaEnvelope, FaMapMarkerAlt } from 'react-icons/fa';
import client from '../sanityClient';
import { contactPageQuery, tandaraDetailsQuery } from '../queries';
import { getStaticPageData } from '../utils/staticDataLoader';

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem 3rem;
`;

const ContactGrid = styled.div`
  margin-top: 2rem;
`;

const ContactInfo = styled.div`
  background-color: #f5f7fa;
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const ContactTitle = styled.h2`
  color: #2E5984;
  margin-bottom: 1.5rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -0.5rem;
    width: 50px;
    height: 2px;
    background-color: #F5A623;
  }
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ContactIcon = styled.div`
  width: 40px;
  height: 40px;
  background-color: #2E5984;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
`;

const ContactText = styled.div`
  flex-grow: 1;
  
  h3 {
    font-size: 1rem;
    margin-bottom: 0.25rem;
    color: #2E5984;
  }
  
  p, a {
    margin: 0;
    color: #666;
  }
  
  a {
    text-decoration: none;
    transition: color 0.3s ease;
    
    &:hover {
      color: #2E5984;
    }
  }
`;

const MapContainer = styled.div`
  width: 100%;
  height: 450px;
  margin-top: 3rem;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const Contact = () => {
  // Default content to show immediately (no loading state)
  const defaultContactData = {
    title: "Contact Us",
    subtitle: "Get in touch with Tandara Lodge",
    contactTitle: "Contact Information",
    addressTitle: "Address",
    phoneTitle: "Phone",
    faxTitle: "Fax",
    emailTitle: "Email"
  };

  const defaultTandaraDetails = {
    streetAddress: "10 Nightingale Avenue, Sheffield, Tasmania 7306",
    phoneNumber: "03 6491 1277",
    faxNumber: "03 6491 2527",
    emailAddress: "admin@tandara.net.au"
  };

  const [contactPageData, setContactPageData] = useState(defaultContactData);
  const [tandaraDetailsData, setTandaraDetailsData] = useState(defaultTandaraDetails);

  useEffect(() => {
    const loadData = async () => {
      try {
        // Try to load static data first
        const staticContactData = await getStaticPageData('contactPage');
        const staticTandaraData = await getStaticPageData('tandaraDetails');
        
        if (staticContactData) {
          setContactPageData(staticContactData);
        }
        if (staticTandaraData) {
          setTandaraDetailsData(staticTandaraData);
        }
        
        // If no static data, fallback to runtime fetching
        if (!staticContactData || !staticTandaraData) {
          // Fetch contact page data
          if (!staticContactData) {
            const contactData = await client.fetch(contactPageQuery);
            if (contactData) {
              setContactPageData(contactData);
            }
          }
          
          // Fetch tandara details data
          if (!staticTandaraData) {
            const tandaraData = await client.fetch(tandaraDetailsQuery);
            if (tandaraData) {
              setTandaraDetailsData(tandaraData);
            }
          }
        }
      } catch (error) {
        console.error('Error loading contact page data:', error);
        // Keep default content if all fails
      }
    };

    loadData();
  }, []);

  return (
    <div>
      <PageHeader 
        title={contactPageData.title} 
        subtitle={contactPageData.subtitle}
      />
      
      <PageContainer>
        <ContactGrid>
          <ContactInfo>
            <ContactTitle>
              {contactPageData.contactTitle}
            </ContactTitle>
            
            <ContactItem>
              <ContactIcon>
                <FaMapMarkerAlt />
              </ContactIcon>
              <ContactText>
                <h3>{contactPageData.addressTitle}</h3>
                <p>{tandaraDetailsData.streetAddress}</p>
              </ContactText>
            </ContactItem>
            
            <ContactItem>
              <ContactIcon>
                <FaPhone />
              </ContactIcon>
              <ContactText>
                <h3>{contactPageData.phoneTitle}</h3>
                <p>
                  <a href={`tel:${tandaraDetailsData.phoneNumber}`}>
                    {tandaraDetailsData.phoneNumber}
                  </a>
                </p>
              </ContactText>
            </ContactItem>
            
            <ContactItem>
              <ContactIcon>
                <FaFax />
              </ContactIcon>
              <ContactText>
                <h3>{contactPageData.faxTitle}</h3>
                <p>{tandaraDetailsData.faxNumber}</p>
              </ContactText>
            </ContactItem>
            
            <ContactItem>
              <ContactIcon>
                <FaEnvelope />
              </ContactIcon>
              <ContactText>
                <h3>{contactPageData.emailTitle}</h3>
                <p>
                  <a href={`mailto:${tandaraDetailsData.emailAddress}`}>
                    {tandaraDetailsData.emailAddress}
                  </a>
                </p>
              </ContactText>
            </ContactItem>
          </ContactInfo>
        </ContactGrid>
        
        <MapContainer>
          <iframe 
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2828.3861676610236!2d146.32292307670767!3d-41.38114997129825!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xaa7b0b6a7d4a3c8f%3A0x5c3b3d8d6c97f8a7!2s10%20Nightingale%20Ave%2C%20Sheffield%20TAS%207306%2C%20Australia!5e0!3m2!1sen!2sus!4v1711359000000!5m2!1sen!2sus" 
            width="100%" 
            height="100%" 
            style={{ border: 0 }} 
            allowFullScreen="" 
            loading="lazy" 
            referrerPolicy="no-referrer-when-downgrade"
            title="Tandara Lodge Location"
          ></iframe>
        </MapContainer>
      </PageContainer>
    </div>
  );
};

export default Contact;
